/**
 * 인증 관련 유틸리티 함수
 */
const AuthUtils = {
  /**
   * 로컬 스토리지에서 액세스 토큰을 가져옵니다.
   * @returns {string|null} 저장된 액세스 토큰 또는 없는 경우 null
   */
  getAccessToken: () => {
    return localStorage.getItem('jwt_access_token');
  },

  /**
   * 로컬 스토리지에서 리프레시 토큰을 가져옵니다.
   * @returns {string|null} 저장된 리프레시 토큰 또는 없는 경우 null
   */
  getRefreshToken: () => {
    return localStorage.getItem('jwt_refresh_token');
  },

  /**
   * 로컬 스토리지에 액세스 토큰을 저장합니다.
   * @param {string} token 저장할 액세스 토큰
   */
  setAccessToken: (token) => {
    localStorage.setItem('jwt_access_token', token);
  },

  /**
   * 로컬 스토리지에 리프레시 토큰을 저장합니다.
   * @param {string} token 저장할 리프레시 토큰
   */
  setRefreshToken: (token) => {
    localStorage.setItem('jwt_refresh_token', token);
  },

  /**
   * 모든 인증 토큰을 제거합니다. (로그아웃시 사용)
   */
  clearTokens: () => {
    localStorage.removeItem('jwt_access_token');
    localStorage.removeItem('jwt_refresh_token');
  },

  /**
   * 사용자가 로그인 상태인지 확인합니다.
   * @returns {boolean} 로그인 상태 여부
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('jwt_access_token');
  },

  /**
   * 리프레시 토큰을 사용하여 새로운 액세스 토큰을 요청합니다.
   * @returns {Promise<string|null>} 새로운 액세스 토큰 또는 오류 발생시 null
   */
  refreshAccessToken: async () => {
    const refreshToken = AuthUtils.getRefreshToken();
    
    if (!refreshToken) {
      console.error('리프레시 토큰이 없습니다.');
      return null;
    }
    
    try {
      const response = await fetch('/api_Token/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const newAccessToken = data.accessToken;
        AuthUtils.setAccessToken(newAccessToken);
        return newAccessToken;
      } else {
        console.error('토큰 갱신 실패:', data.message);
        // 토큰 갱신 실패시 로그아웃 처리
        AuthUtils.clearTokens();
        return null;
      }
    } catch (error) {
      console.error('토큰 갱신 요청 실패:', error);
      return null;
    }
  },

  /**
   * 인증이 필요한 API 요청을 보냅니다.
   * 401 에러 발생시 토큰 갱신 후 재시도합니다.
   * @param {string} url 요청 URL
   * @param {object} options fetch 옵션
   * @returns {Promise<Response>} fetch 응답
   */
  authenticatedFetch: async (url, options = {}) => {
    // 헤더에 인증 토큰 추가
    const accessToken = AuthUtils.getAccessToken();
    
    if (!accessToken) {
      throw new Error('인증 토큰이 없습니다. 로그인이 필요합니다.');
    }
    
    const authOptions = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${accessToken}`
      }
    };
    
    try {
      let response = await fetch(url, authOptions);
      
      // 토큰 만료시 갱신 후 재시도
      if (response.status === 401) {
        const newToken = await AuthUtils.refreshAccessToken();
        
        if (newToken) {
          authOptions.headers['Authorization'] = `Bearer ${newToken}`;
          response = await fetch(url, authOptions);
        } else {
          throw new Error('토큰 갱신에 실패했습니다. 다시 로그인해주세요.');
        }
      }
      
      return response;
    } catch (error) {
      console.error('인증된 요청 실패:', error);
      throw error;
    }
  }
};

export default AuthUtils; 